import { useEffect, useState } from 'react'
import { IconMenu, IconMoon, IconSun } from '../icons'
import { useAuth } from '../../context/AuthContext'
import { useUI } from '../../context/UIContext'
import { useTheme } from '../theme-provider'
import { platformScopeBadgeLabel } from '../../lib/platformAdminAccess'

export function Navbar({ title, subtitle }) {
  const { user, logout, loading } = useAuth()
  const { toggleSidebar } = useUI()
  const { setTheme, resolvedTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const displayName = user?.name || 'Admin'
  const initial = displayName.charAt(0).toUpperCase()
  const isDark = resolvedTheme === 'dark'

  const scopeBadge = platformScopeBadgeLabel(user?.adminScope)

  return (
    <header className="glass z-20 flex h-16 shrink-0 items-center justify-between gap-4 border-b border-border px-4 sm:px-6 lg:px-8">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <button
          type="button"
          onClick={toggleSidebar}
          className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-border bg-card/80 text-foreground shadow-sm transition hover:bg-muted lg:hidden"
          aria-label="Open menu"
        >
          <IconMenu className="h-5 w-5" aria-hidden />
        </button>

        <div className="min-w-0 py-1">
          <h1 className="truncate text-lg font-semibold tracking-tight text-foreground">{title}</h1>
          {subtitle ? (
            <p className="mt-0.5 truncate text-sm text-muted-foreground">{subtitle}</p>
          ) : null}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2 sm:gap-3">
        {scopeBadge ? (
          <span className="hidden rounded-full border border-border bg-muted/60 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground sm:inline-flex">
            {scopeBadge}
          </span>
        ) : null}
        <button
          type="button"
          onClick={() => setTheme(isDark ? 'light' : 'dark')}
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-xl border border-border bg-card/80 text-foreground shadow-sm transition hover:bg-muted disabled:opacity-50"
          disabled={!mounted}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {mounted ? (
            isDark ? (
              <IconSun className="h-5 w-5" aria-hidden />
            ) : (
              <IconMoon className="h-5 w-5" aria-hidden />
            )
          ) : (
            <IconMoon className="h-5 w-5 opacity-50" aria-hidden />
          )}
        </button>

        <div className="flex items-center gap-2 sm:hidden">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground ring-2 ring-card">
            {initial}
          </div>
          <span className="max-w-[7rem] truncate text-xs font-medium text-foreground">{displayName}</span>
        </div>

        <div className="hidden items-center gap-3 sm:flex">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-sm font-semibold text-muted-foreground ring-2 ring-card">
            {initial}
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-foreground">{displayName}</p>
            {user?.email ? (
              <p className="max-w-[200px] truncate text-xs text-muted-foreground">{user.email}</p>
            ) : null}
          </div>
        </div>

        <button
          type="button"
          onClick={() => logout()}
          disabled={loading}
          className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground shadow-sm transition hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
        >
          Logout
        </button>
      </div>
    </header>
  )
}
